import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getStorage, ref, listAll, getDownloadURL } from 'firebase/storage';
import { Avatar } from '@mui/material';
import ProfileSettings from '../components/profile/profileSettings';
import OrderHistory from '../components/profile/orderHistory';
import ImageHistory from '../components/profile/imageHistory';
import InvoiceHistory from '../components/profile/invoiceHistory';
import ArtHistory from '../components/profile/artRequests';
import { HiOutlineUserCircle, HiOutlineClock, HiOutlinePhoto, HiOutlinePaintBrush, HiOutlineDocumentText } from 'react-icons/hi2';
import './CustomerProfile.scss';

const pages = [
  { name: 'Profile', valueKey: 'profile', Icon: HiOutlineUserCircle },
  { name: 'DTF Orders', valueKey: 'orders', Icon: HiOutlineClock },
  { name: 'Images', valueKey: 'images', Icon: HiOutlinePhoto },
  { name: 'Art Requests', valueKey: 'art', Icon: HiOutlinePaintBrush },
  { name: 'Invoices', valueKey: 'invoices', Icon: HiOutlineDocumentText },
];

function CustomerProfile() {
  const [userData, setUserData] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [stackedOrderIds, setStackedOrderIds] = useState(null);
  const [userPictures, setUserPictures] = useState([]);
  const [userInvoices, setInvoiceFiles] = useState([]); // kept for future use if needed
  const [useArtOrders, setUserArtOrders] = useState([]);
  const [selectedPage, setSelectedPage] = useState('profile');
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const isNet30Approved = Boolean(userData?.net30Approved);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData({ uid: user.uid, ...userDoc.data() });
        }
        setLoading(false);
      } else {
        setUserData(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (userData?.uid) {
        const ordersRef = collection(db, 'orders');
        const qy = query(ordersRef, where('customerId', '==', userData.uid));
        const snapshot = await getDocs(qy);
        const orders = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUserOrders(orders);
      }
    };
    fetchOrders();
  }, [userData]);

  useEffect(() => {
    const fetchStackedOrders = async () => {
      // Only attempt to read stackedOrders for users that are allowed to have them
      // (e.g., Net 30 / consolidated invoicing enabled).
      if (!userData?.uid || !userData?.net30Approved) {
        setStackedOrderIds([]);
        return;
      }
      try {
        const stackedSnap = await getDoc(doc(db, 'stackedOrders', userData.uid));
        if (!stackedSnap.exists()) {
          setStackedOrderIds([]);
          return;
        }
        const data = stackedSnap.data() || {};
        let orderIds = [];
        if (Array.isArray(data.orders)) {
          orderIds = data.orders;
        } else if (data.orders && typeof data.orders === 'object') {
          orderIds = Object.keys(data.orders);
        }
        setStackedOrderIds(orderIds);
      } catch (error) {
        console.error('Error fetching stackedOrders:', error);
        setStackedOrderIds([]);
      }
    };
    fetchStackedOrders();
  }, [userData]);

  useEffect(() => {
    const fetchArtOrders = async () => {
      if (userData?.uid) {
        const ordersRef = collection(db, 'artOrders');
        const qy = query(ordersRef, where('customerId', '==', userData.uid));
        const snapshot = await getDocs(qy);
        const orders = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUserArtOrders(orders);
      }
    };
    fetchArtOrders();
  }, [userData]);

  // Only load user picture URLs when the Images tab is active
  useEffect(() => {
    const fetchUserPictures = async () => {
      if (!userData?.uid) return;
      if (selectedPage !== 'images') return;

      const storage = getStorage();
      const userRef = ref(storage, `pictures/${userData.uid}`);
      try {
        const result = await listAll(userRef);
        const sortedItems = result.items.sort((a, b) => a.name.localeCompare(b.name));
        const pictureURLs = await Promise.all(
          sortedItems.map((item) => getDownloadURL(item))
        );
        setUserPictures(pictureURLs);
      } catch (error) {
        console.error('Error fetching user pictures:', error);
      }
    };
    fetchUserPictures();
  }, [userData, selectedPage]);

  // Fetch invoice files (kept for parity; not displayed directly here)
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!userData?.uid || !isNet30Approved) {
        setInvoiceFiles([]);
        return;
      }
      if (userData?.uid) {
        const storage = getStorage();
        const invoiceRef = ref(storage, `invoices/${userData.uid}`);
        try {
          const result = await listAll(invoiceRef);
          const files = await Promise.all(
            result.items.map(async (item) => {
              const url = await getDownloadURL(item);
              return {
                name: item.name,
                url,
                time: item.name.match(/\d+/)?.[0] || '',
              };
            })
          );
          setInvoiceFiles(files.sort((a, b) => b.time.localeCompare(a.time)));
        } catch (error) {
          console.error('Error fetching invoices:', error);
        }
      }
    };
    fetchInvoices();
  }, [userData, isNet30Approved]);

  // Sync selected page and order with URL
  useEffect(() => {
    const path = location.pathname || '';
    const afterProfile = path.startsWith('/profile') ? path.slice('/profile'.length) : path;
    const parts = afterProfile.split('/').filter(Boolean);
    if (parts[0] === 'orders') {
      setSelectedPage('orders');
      setSelectedOrderId(parts[1] || '');
    } else if (parts[0] === 'images') {
      setSelectedPage('images');
      setSelectedOrderId('');
    } else if (parts[0] === 'art') {
      setSelectedPage('art');
      setSelectedOrderId('');
    } else if (parts[0] === 'invoices') {
      if (isNet30Approved) {
        setSelectedPage('invoices');
        setSelectedOrderId('');
      } else {
        setSelectedPage('profile');
        setSelectedOrderId('');
        navigate('/profile', { replace: true });
      }
    } else {
      setSelectedPage('profile');
      setSelectedOrderId('');
    }
  }, [location.pathname, isNet30Approved, navigate]);

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        window.location.href = '/login';
      })
      .catch((error) => {
        console.error('Logout error:', error);
      });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <h3>User not found or not logged in.</h3>
      </div>
    );
  }

  const visiblePages = isNet30Approved
    ? pages
    : pages.filter((p) => p.valueKey !== 'invoices');

  // Navigation helpers
  const goTo = (segment) => {
    if (segment === 'orders') navigate('/profile/orders');
    else if (segment === 'images') navigate('/profile/images');
    else if (segment === 'art') navigate('/profile/art');
    else if (segment === 'invoices') {
      if (!isNet30Approved) return;
      navigate('/profile/invoices');
    }
    else navigate('/profile');
  };

  // Simple derived stats for helpful overview
  const totalOrders = Array.isArray(userOrders) ? userOrders.length : 0;
  const openOrders = Array.isArray(userOrders)
    ? userOrders.filter((o) => {
      const status = String(o.status || '').toLowerCase();
      return status && !['completed', 'cancelled', 'refunded'].includes(status);
    }).length
    : 0;
  const lastOrderDate =
    Array.isArray(userOrders) && userOrders.length
      ? (() => {
        const toDate = (val) => {
          if (!val) return null;
          if (typeof val.toDate === 'function') {
            try {
              return val.toDate();
            } catch (e) {
              return null;
            }
          }
          if (val instanceof Date) return val;
          return new Date(val);
        };
        const dates = userOrders
          .map((o) => toDate(o.createdAt))
          .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()));
        if (!dates.length) return null;
        const latest = dates.reduce((a, b) => (a > b ? a : b));
        return latest.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      })()
      : null;

  const imagesCount = Array.isArray(userPictures) ? userPictures.length : 0;
  const artRequestsCount = Array.isArray(useArtOrders) ? useArtOrders.length : 0;

  const firstName =
    (userData &&
      typeof userData.firstName === 'string' &&
      userData.firstName.trim()) ||
    (userData &&
      typeof userData.freshbooksClientId === 'object' &&
      userData.freshbooksClientId &&
      userData.freshbooksClientId.organization) ||
    'there';

  return (
    <div className="main-container cp-container">
      <div className="cp-header">

        <Avatar
          src={userData.profileImageURL || ''}
          sx={{ width: 72, height: 72 }}
        />

        <div className="cp-header__title">
          <span className="cp-card__eyebrow">Customer overview</span>
          <span className="cp-card__title">Welcome back, {firstName}</span>
        </div>
      </div>

      <div className="cp-mobile-nav" aria-label="Profile navigation">
        {visiblePages.map(({ name, valueKey, Icon }) => (
          <button
            key={valueKey}
            className={`nav-item ${selectedPage === valueKey ? 'active' : ''}`}
            onClick={() => goTo(valueKey)}
            aria-label={name}
          >
            <span className="nav-icon">{Icon ? <Icon /> : null}</span>
            <span>{name}</span>
          </button>
        ))}
      </div>

      <div className="admin-container cp-layout">
        <div className="sidebar">
          <div className="nav-list">
            {visiblePages.map(({ name, valueKey, Icon }) => (
              <button
                key={valueKey}
                className={`nav-item ${selectedPage === valueKey ? 'active' : ''}`}
                onClick={() => goTo(valueKey)}
              >
                <span className="nav-icon">{Icon ? <Icon /> : null}</span>
                <span>{name}</span>
              </button>
            ))}
            <button className="nav-item" onClick={handleLogout}>
              <span className="nav-icon">{/* sign out icon optional */}</span>
              <span>Logout</span>
            </button>
          </div>
        </div>

        <div className="cp-main-content">
          <div className="ao-page">
            <div className="ao-shell">
              {selectedPage === 'profile' && (
                <>
                  <div className="cp-card">
                    <ProfileSettings userData={userData} handleLogout={handleLogout} />
                  </div>
                </>
              )}

              {selectedPage === 'orders' && (
                <div className="cp-card">

                  <OrderHistory
                    userOrders={userOrders}
                    stackedOrderIds={stackedOrderIds}
                    selectedOrderId={selectedOrderId}
                    onOrderOpen={(id) => navigate(`/profile/orders/${id}`)}
                    onOrderClose={() => navigate('/profile/orders')}
                  />
                </div>
              )}

              {selectedPage === 'images' && (
                <div className="cp-card">
                  <ImageHistory
                    userPictures={userPictures}
                    selectedImage={selectedImage}
                    setSelectedImage={setSelectedImage}
                  />
                </div>
              )}

              {selectedPage === 'art' && (
                <div className="cp-card">

                  <ArtHistory userOrders={useArtOrders} />
                </div>
              )}

              {selectedPage === 'invoices' && isNet30Approved && (
                <div className="cp-card">
                  <InvoiceHistory
                    userOrders={userOrders}
                    stackedOrderIds={stackedOrderIds}
                    freshbooksClientId={userData?.freshbooksClientId}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}

export default CustomerProfile;
